import { type FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import { PdpRespStorytellingText } from './PdpRespStorytellingText.tsx';
import { PdpSharedStorytellingMainMedia } from '../PdpSharedStorytellingComponents/PdpSharedStorytellingMainMedia.tsx';
import { PdpSharedStorytellingShopAllLink } from '../PdpSharedStorytellingComponents/PdpSharedStorytellingShopAllLink.tsx';
import {
    pdpAccordionReadMoreButtonClass,
    pdpAccordionReadMoreWrapperClass,
} from '../PdpRespDetailAccordions/PdpRespAccordionReadMoreLabels.tsx';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';

import { getSrcsetString } from 'dibs-image-utils/exports/srcSet.ts';

import { type PdpRespStorytellingTab_storyTellingTab$data } from './__generated__/PdpRespStorytellingTab_storyTellingTab.graphql.ts';

import classnames from 'classnames';

import dibsCss from 'dibs-css';
import textStyles from '../PdpSharedStorytellingComponents/PdpSharedStorytellingText.scss';
import styles from './PdpRespStorytellingModule.scss';

type Props = {
    storyTellingTab: PdpRespStorytellingTab_storyTellingTab$data;
    clearBackground: boolean;
    wrapperClass?: string;
    showExpandingArea?: boolean;
    showHeader?: boolean;
    dataTn?: string;
};

const PdpRespStorytellingTabComponent: FC<Props> = ({
    storyTellingTab,
    clearBackground,
    wrapperClass,
    showExpandingArea,
    showHeader = true,
    dataTn,
}) => {
    const { isPdpItemModuleTestVariant } = useServerVarsContext();
    const { smallImage, mainImage, mainVideo } = storyTellingTab;
    const expandingAreaWrapperClass = classnames(
        isPdpItemModuleTestVariant && pdpAccordionReadMoreWrapperClass,
        textStyles.expandingAreaWrapper
    );
    const expandingAreaButtonClass = isPdpItemModuleTestVariant
        ? pdpAccordionReadMoreButtonClass
        : undefined;
    const largeImageAndText = mainImage || mainVideo;

    return (
        <div
            className={classnames(
                styles.tabContainer,
                dibsCss.bgMoonstone,
                dibsCss.pLarger,
                dibsCss.mtSmall,
                dibsCss.mx0,
                dibsCss.mbXlarge,
                wrapperClass,
                {
                    [styles.clearBackground]: !!clearBackground,
                }
            )}
            data-tn={dataTn}
        >
            <div
                className={classnames(
                    styles.innerTabContainer,
                    dibsCss.maxW1440px,
                    dibsCss.mAuto,
                    dibsCss.grid
                )}
            >
                {smallImage && (
                    <div
                        className={classnames(
                            dibsCss.flex,
                            dibsCss.flexCol,
                            dibsCss.itemsCenter,
                            dibsCss.sassyFontBody,
                            dibsCss.textCenter,
                            styles.smallImageCol
                        )}
                    >
                        <div
                            className={classnames(
                                styles.smallImageWrapper,
                                dibsCss.flex,
                                dibsCss.justifyCenter,
                                dibsCss.itemsCenter,
                                dibsCss.mbSmall,
                                dibsCss.bgWhite
                            )}
                        >
                            <img
                                className={styles.smallImage}
                                src={smallImage}
                                srcSet={getSrcsetString(smallImage)}
                                sizes="20vw"
                                alt=""
                            />
                        </div>
                        <PdpSharedStorytellingShopAllLink storyTellingTab={storyTellingTab} />
                    </div>
                )}
                <div
                    className={classnames(
                        styles.textCol,
                        dibsCss.sassyFontBody,
                        dibsCss.flex,
                        dibsCss.flexCol,
                        dibsCss.justifyCenter
                    )}
                >
                    <PdpRespStorytellingText
                        storyTellingTab={storyTellingTab}
                        showExpandingArea={showExpandingArea}
                        showHeader={showHeader}
                        disableScrollOnCollapse={isPdpItemModuleTestVariant}
                        expandingAreaWrapperClass={expandingAreaWrapperClass}
                        expandingAreaButtonClass={expandingAreaButtonClass}
                    />
                </div>
                {(mainImage || mainVideo) && (
                    <div
                        className={classnames(
                            dibsCss.flex,
                            dibsCss.flexCol,
                            dibsCss.itemsCenter,
                            dibsCss.sassyFontBody,
                            dibsCss.textCenter,
                            styles.mainImageCol,
                            largeImageAndText && styles.largeImageAndText
                        )}
                    >
                        <PdpSharedStorytellingMainMedia storyTellingTab={storyTellingTab} />
                    </div>
                )}
            </div>
        </div>
    );
};

export const PdpRespStorytellingTab = createFragmentContainer(PdpRespStorytellingTabComponent, {
    storyTellingTab: graphql`
        fragment PdpRespStorytellingTab_storyTellingTab on StoryTellingTab {
            ...PdpRespStorytellingText_storyTellingTab
            ...PdpSharedStorytellingMainMedia_storyTellingTab
            ...PdpSharedStorytellingShopAllLink_storyTellingTab
            smallImage
            mainImage
            mainVideo
        }
    `,
});
